import axios, { AxiosError } from "axios";
import { useEffect, useState, useCallback } from "react";
import ApiConstants from "../../../api-constants";
import { HoleDetail } from "../../../Types/HoleDetail";

function useGolfCourseHoleDetails(golfcourseId?: number) {
  const [golfCourseHoleDetails, setGolfCourseHoleDetails] = useState<
    HoleDetail[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const getGolfCourseHoleDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await axios.get(ApiConstants.golfCourseHoleDetails, {
        headers: {
          Authorization: ApiConstants.authToken,
        },
        params: {
          include: "tees",
          where: {
            golfcourseId,
          },
        },
      });

      if (res.status === 200) {
        setGolfCourseHoleDetails(res.data.results);
      } else {
        throw new AxiosError(`Unexpected response status: ${res.status}`);
      }
    } catch (err) {
      setError(err as AxiosError);
      console.error("Error fetching golf course hole details:", err);
    } finally {
      setIsLoading(false);
    }
  }, [golfcourseId]);

  useEffect(() => {
    if (golfcourseId) {
      getGolfCourseHoleDetails();
    }
  }, [golfcourseId, getGolfCourseHoleDetails]);

  return { golfCourseHoleDetails, isLoading, error };
}

export const customParamsSerializer = (params: any) => {
  return Object.entries(removeEmptyAndNullValues(params))
    .map(([key, value]) => {
      if (typeof value === "object") {
        return `${key}=${encodeURIComponent(JSON.stringify(value))}`;
      }
      return `${key}=${encodeURIComponent(value?.toString() ?? "")}`;
    })
    .join("&");
};

export const removeEmptyAndNullValues = (
  obj: Record<string, any>
): Record<string, any> => {
  return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
    const value = obj[key];

    if (value !== "" && value !== null) {
      if (Array.isArray(value)) {
        const filteredArray = value
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              return removeEmptyAndNullValues(item);
            }
            return item;
          })
          .filter((item) => {
            if (typeof item === "object" && item !== null) {
              return Object.keys(item).length !== 0;
            }
            return item !== "" && item !== null;
          });

        if (filteredArray.length !== 0) {
          acc[key] = filteredArray;
        }
      } else if (typeof value === "object" && value !== null) {
        const nested = removeEmptyAndNullValues(value);
        if (Object.keys(nested).length !== 0) {
          acc[key] = nested;
        }
      } else {
        acc[key] = value;
      }
    }

    return acc;
  }, {} as Record<string, any>);
};

export default useGolfCourseHoleDetails;
